import encodeQR from "qr";
import type {
    PurchasedItineraryEntry,
    PurchasedPaymentMethod,
    StoredPurchasedTicket,
} from "../../store/persistence/slices/purchasedItineraries.slice";
import { getPurchasedSegmentData } from "../../tools/purchasedItineraries.tools";
import type { ItineraryDTO } from "../../types/dtos/api/itinerary/itinerary.dto";
import type { TicketDetailsDTO } from "../../types/dtos/api/purchase/ticket-details.dto";

/**
 * One ticket card of the viewer: the stored ticket plus the display context of
 * its purchased segment (origin/destination recap, price fallback).
 */
export interface TicketViewerItem {

    /** Stable React/Swiper key — the provider ticket id. */
    "key":    string;
    "ticket": StoredPurchasedTicket;

    /** `offer.segments` key the ticket belongs to — `null` for unmatched tickets. */
    "segmentId":   string | null;
    "isUnmatched": boolean;
    "fromName"?:   string;
    "fromTime"?:   string;
    "toName"?:     string;
    "toTime"?:     string;
    "priceCents"?: number;
    "productId"?:  string | null;

    /** Issued-ticket metadata (validity dates, product, passenger…) — `null` until backend enrichment ran. */
    "details"?: TicketDetailsDTO | null;

    /** Payment method summary of the purchase (booking-level, shared by every card) — `null` when unknown. */
    "paymentMethod"?: PurchasedPaymentMethod | null;
}

/**
 * How the barcode zone of a ticket card is rendered.
 */
export type BarcodeRender =
  | {
      "kind":    "qr";
      "dataUrl": string;
  } |
  {
      "kind":    "image";
      "dataUrl": string;
  } |
  { "kind": "unavailable" } |
  {
      "kind":   "unsupported";
      "format": string;
  };

/** `image_*` ticket formats → image mime type for `data:` URIs. */
const IMAGE_FORMAT_MIMES: Record<string, string> = {
    "image_png":  "image/png",
    "image_jpeg": "image/jpeg",
    "image_svg":  "image/svg+xml",
};

/**
 * Decodes a base64 string to bytes, preserving every octet.
 *
 * @param value Base64-encoded payload.
 * @returns Byte array — `null` when the input is not valid base64.
 */
export const base64ToBytes = ( value: string ): Uint8Array | null => {
    try {
        const binary = atob( value );
        const bytes = new Uint8Array( binary.length );

        for ( let index = 0; index < binary.length; index += 1 ) {
            bytes[index] = binary.charCodeAt( index );
        }

        return bytes;
    } catch {
        return null;
    }
};

/**
 * Resolves how a ticket barcode is displayed.
 *
 * @param ticket Stored ticket carrying `ticketFormat`/`ticketData` (base64).
 * @returns Render decision — never throws; corrupt or missing data degrades to a placeholder.
 */
export const resolveBarcodeRender = ( ticket: StoredPurchasedTicket ): BarcodeRender => {
    const format = ticket.ticketFormat;
    const data = ticket.ticketData;

    if ( format == null || data == null ) {
        return { "kind": "unavailable" };
    }

    if ( format === "qr_payload" ) {
        const bytes = base64ToBytes( data );

        if ( bytes === null || bytes.length === 0 ) {
            return { "kind": "unavailable" };
        }

        /*
         * Byte-exact encoding: the payload (e.g. UIC 918.3) is binary. The
         * latin-1 string is a 1:1 byte view and the custom encoder hands those
         * exact octets back to the QR encoder — a UTF-8 textEncoder would
         * corrupt bytes ≥ 0x80.
         */
        const latin1 = Array.from( bytes, ( byte ) => String.fromCharCode( byte ) ).join( "" );

        return {
            "kind":    "qr",
            "dataUrl": encodeQR( latin1, "data-url", {
                "encoding":    "byte",
                "ecc":         "low",
                "border":      4,
                "scale":       1,
                "textEncoder": ( text ) => Uint8Array.from( text, ( char ) => char.charCodeAt( 0 ) & 0xff ),
            } ),
        };
    }

    const imageMime = IMAGE_FORMAT_MIMES[format];

    if ( imageMime !== undefined ) {
        return {
            "kind":    "image",
            "dataUrl": `data:${ imageMime };base64,${ data }`,
        };
    }

    return {
        "kind": "unsupported",
        format,
    };
};

/** Display label of a leg endpoint — waypoint name first, stop name as fallback. */
const legStepLabel = ( itinerary: ItineraryDTO, legIndex: number, direction: "from" | "to" ): string | undefined => {
    const step = itinerary.legs[legIndex]?.[direction];

    return step?.name ?? step?.stop?.name;
};

/**
 * Builds the ordered card list of a purchased itinerary.
 *
 * @param entry Persisted purchased itinerary.
 * @returns Tickets in itinerary order (pricing segment order, then ticket order),
 * followed by unmatched tickets flagged `isUnmatched`.
 */
export const buildTicketViewerItems = ( entry: PurchasedItineraryEntry ): TicketViewerItem[] => {
    const items: TicketViewerItem[] = [];

    for ( const segment of entry.itinerary.pricingSegments ?? [] ) {
        const purchased = getPurchasedSegmentData( entry.itinerary, segment.id );

        if ( purchased === undefined ) {
            continue;
        }

        const firstLegIndex = segment.legRange?.firstLegIndex;
        const lastLegIndex = segment.legRange?.lastLegIndex;
        const fromName = firstLegIndex === undefined ? undefined : legStepLabel( entry.itinerary, firstLegIndex, "from" );
        const fromTime = firstLegIndex === undefined ? undefined : entry.itinerary.legs[firstLegIndex]?.from?.time;
        const toName = lastLegIndex === undefined ? undefined : legStepLabel( entry.itinerary, lastLegIndex, "to" );
        const toTime = lastLegIndex === undefined ? undefined : entry.itinerary.legs[lastLegIndex]?.to?.time;

        for ( const ticket of purchased.tickets ) {
            items.push( {
                "key":           ticket.providerTicketId,
                ticket,
                "segmentId":     segment.id,
                "isUnmatched":   false,
                fromName,
                fromTime,
                toName,
                toTime,
                "priceCents":    purchased.priceCents,
                "productId":     purchased.productId,
                "details":       ticket.details ?? null,
                "paymentMethod": entry.purchase.paymentMethod ?? null,
            } );
        }
    }

    for ( const ticket of entry.purchase.unmatchedTickets ) {
        items.push( {
            "key":           ticket.providerTicketId,
            ticket,
            "segmentId":     null,
            "isUnmatched":   true,
            "details":       ticket.details ?? null,
            "paymentMethod": entry.purchase.paymentMethod ?? null,
        } );
    }

    return items;
};

/**
 * Formats a `YYYY-MM-DD` calendar date as `dd.mm.yyyy` without `Date` parsing —
 * provider ticket dates carry no timezone, so they must never shift a day.
 *
 * @param value Provider date (`YYYY-MM-DD`).
 * @returns Formatted date, or `""` for malformed input.
 */
export const formatTicketDay = ( value: string ): string => {
    const match = ( /^(\d{4})-(\d{2})-(\d{2})$/u ).exec( value );

    return match === null ? "" : `${ match[3] }.${ match[2] }.${ match[1] }`;
};

/**
 * Display label of a provider quality-of-service code.
 *
 * @param qualityOfService Raw provider code (e.g. `ZWEITE_KLASSE`).
 * @returns Short French label — `null` for unknown codes (chip is hidden).
 */
export const formatQualityOfService = ( qualityOfService: string | undefined ): string | null => {
    switch ( qualityOfService ) {
        case "ZWEITE_KLASSE":
            return "2e cl.";

        case "ERSTE_KLASSE":
            return "1re cl.";

        default:
            return null;
    }
};

/**
 * One bound of the offer's `ticketValidity`, split for display.
 */
interface TicketValidityPoint {
    "day":  string;
    "time": string | null;
}

/**
 * Splits a `ticketValid*` bound into its display parts without `Date` parsing —
 * provider times carry no timezone and must be shown verbatim.
 *
 * @param value `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm[:ss]`.
 * @returns Formatted day (`dd.mm.yyyy`) + `HH:mm` time, or `null` for malformed input.
 */
const splitTicketValidityPoint = ( value: string ): TicketValidityPoint | null => {
    const [ date, time ] = value.split( "T" );
    const day = date === undefined ? "" : formatTicketDay( date );

    if ( day === "" ) {
        return null;
    }

    const shortTime = time !== undefined && ( /^\d{2}:\d{2}/u ).test( time ) ? time.slice( 0, 5 ) : null;

    return {
        day,
        "time": shortTime,
    };
};

/**
 * Formats one bound for a `du … au …` sentence: day plus time when known.
 */
const formatValidityBound = ( point: TicketValidityPoint ): string => {
    return point.time === null ? point.day : `${ point.day } ${ point.time }`;
};

/**
 * Validity line of an issued ticket. The purchased offer's `ticketValidity`
 * (`ticketValidFrom`/`ticketValidTo`, precise to the minute) wins over the
 * date-only issued-ticket fields (`validFrom`/`validTo`).
 *
 * @param details Enriched ticket details — `null`/`undefined` hides the line.
 * @returns The validity sentence, or `""` when no dates are known.
 */
export const formatTicketValidity = ( details: TicketDetailsDTO | null | undefined ): string => {
    const offerFrom = details?.ticketValidFrom === undefined ? null : splitTicketValidityPoint( details.ticketValidFrom );
    const offerTo = details?.ticketValidTo === undefined ? null : splitTicketValidityPoint( details.ticketValidTo );

    if ( offerFrom !== null || offerTo !== null ) {
        const from = offerFrom ?? offerTo;
        const to = offerTo ?? offerFrom;

        if ( from === null || to === null ) {
            return "";
        }

        if ( from.day === to.day ) {
            return from.time !== null && to.time !== null
                ? `Valable le ${ from.day } de ${ from.time } à ${ to.time }`
                : `Valable le ${ from.day }`;
        }

        return `Valable du ${ formatValidityBound( from ) } au ${ formatValidityBound( to ) }`;
    }

    const from = details?.validFrom === undefined ? "" : formatTicketDay( details.validFrom );
    const to = details?.validTo === undefined ? "" : formatTicketDay( details.validTo );

    if ( from === "" && to === "" ) {
        return "";
    }

    if ( from === "" || to === "" || from === to ) {
        return `Valable le ${ from === "" ? to : from }`;
    }

    return `Valable du ${ from } au ${ to }`;
};

/**
 * Both validity bounds of an issued ticket, formatted for a two-line
 * `Du …`/`Au …` display. Same precedence as {@link formatTicketValidity}
 * (offer `ticketValidity` over issued-ticket dates).
 *
 * @param details Enriched ticket details.
 * @returns `{ from, to }` — either side is `null` when the bound is unknown;
 *   both `null` when no validity is known at all.
 */
export const formatTicketValidityBounds = ( details: TicketDetailsDTO | null | undefined ): {
    "from": string | null;
    "to":   string | null;
} => {
    const offerFrom = details?.ticketValidFrom === undefined ? null : splitTicketValidityPoint( details.ticketValidFrom );
    const offerTo = details?.ticketValidTo === undefined ? null : splitTicketValidityPoint( details.ticketValidTo );

    if ( offerFrom !== null || offerTo !== null ) {
        return {
            "from": offerFrom === null ? null : formatValidityBound( offerFrom ),
            "to":   offerTo === null ? null : formatValidityBound( offerTo ),
        };
    }

    const from = details?.validFrom === undefined ? "" : formatTicketDay( details.validFrom );
    const to = details?.validTo === undefined ? "" : formatTicketDay( details.validTo );

    return {
        "from": from === "" ? null : from,
        "to":   to === "" ? null : to,
    };
};

/**
 * Formats the provider sales timestamp for display (`dd.mm.yyyy HH:mm`).
 * Provider-local, no timezone — shown verbatim, never `Date`-parsed.
 *
 * @param salesDate `YYYY-MM-DD` or `YYYY-MM-DDTHH:mm[:ss]`.
 * @returns The formatted timestamp, or `""` for malformed input.
 */
export const formatTicketSalesDate = ( salesDate: string ): string => {
    const point = splitTicketValidityPoint( salesDate );

    return point === null ? "" : formatValidityBound( point );
};

/**
 * Formats a VAT rate for the price line (`8,1 %`) — `vatPercent` is already a
 * percent (provider `vat` divided by 10 by the backend).
 *
 * @param vatPercent VAT rate in percent (e.g. `8.1`).
 * @returns French-formatted percentage.
 */
export const formatTicketVat = ( vatPercent: number ): string => {
    return `${ new Intl.NumberFormat( "fr-FR", { "maximumFractionDigits": 1 } ).format( vatPercent ) } %`;
};

/** Display labels of known payment brands (`visa` → `Visa`); unknown brands are capitalized. */
const PAYMENT_BRAND_LABELS: Record<string, string> = {
    "visa":             "Visa",
    "mastercard":       "Mastercard",
    "american-express": "American Express",
    "amex":             "American Express",
    "maestro":          "Maestro",
    "twint":            "TWINT",
    "postfinance":      "PostFinance",
};

/** Display labels of known wallets (`apple-pay` → `Apple Pay`); unknown wallets are capitalized. */
const PAYMENT_WALLET_LABELS: Record<string, string> = {
    "apple-pay":  "Apple Pay",
    "google-pay": "Google Pay",
};

/**
 * Humanizes a provider payment-method token (`apple-pay` → `Apple Pay` fallback).
 *
 * @param token Raw provider token.
 * @param labels Known-token label map.
 * @returns Display label, or `null` for empty input.
 */
const paymentTokenLabel = ( token: string | null, labels: Record<string, string> ): string | null => {
    if ( token === null || token === "" ) {
        return null;
    }

    const known = labels[token.toLowerCase()];

    return known ?? token.charAt( 0 ).toUpperCase() + token.slice( 1 );
};

/**
 * Formats the payment method for the card back (`Visa (*4242)`,
 * `Apple Pay · Visa (*4242)`, `TWINT`).
 *
 * @param method Payment method summary — `null`/empty hides the line.
 * @returns The display label, or `""` when nothing is known.
 */
export const formatPaymentMethod = ( method: PurchasedPaymentMethod | null | undefined ): string => {
    if ( method === null || method === undefined ) {
        return "";
    }

    const wallet = paymentTokenLabel( method.wallet, PAYMENT_WALLET_LABELS );
    const brand = paymentTokenLabel( method.brand, PAYMENT_BRAND_LABELS );
    const parts = [ wallet, brand ].filter( ( part ): part is string => part !== null );

    if ( parts.length === 0 ) {
        return "";
    }

    const last4 = method.cardLast4 === null || method.cardLast4 === "" ? "" : ` (*${ method.cardLast4 })`;

    return parts.join( " · " ) + last4;
};

/**
 * Cleans the provider product description for display: CFF texts carry literal
 * `.\t` bullet markers that are turned into real bullets.
 *
 * @param description Raw prebooking description.
 * @returns Display text with `•` bullets.
 */
export const formatTicketDescription = ( description: string ): string => {
    return description.
        replace( /^\.\s+/gmu, "• " ).
        trim();
};

/**
 * Index of the first ticket belonging to the requested segment.
 *
 * @param items Viewer items in display order.
 * @param initialSegmentId `offer.segments` key to focus — `null` opens the deck start.
 * @returns Slide index — deterministic `0` fallback.
 */
export const resolveInitialSlideIndex = ( items: readonly TicketViewerItem[], initialSegmentId: string | null ): number => {
    if ( initialSegmentId === null ) {
        return 0;
    }

    const index = items.findIndex( ( item ) => item.segmentId === initialSegmentId );

    return index >= 0 ? index : 0;
};
